import { useCallback } from 'react';
import {
  postSupportTicketAttachments,
  useGetSupportAttachmentPolicy,
} from '@/api/control/control-plane/control-plane';
import type { SupportAttachmentDto, SupportAttachmentPolicyDto } from '@/api/control/missionImpactAPI.schemas';
import type { SupportTranslations } from '@/i18n/types/support';
import { useLanguage } from '@/contexts/LanguageContext';
import { fillSupportTemplate, formatBytes, validateSupportAttachment } from '@/lib/support';

/** Result of uploading a batch of files. */
export interface AttachmentUploadResult {
  uploaded: SupportAttachmentDto[];
  failed: File[];
}

/** Result of validating a batch of picked files. */
export interface AttachmentValidation {
  accepted: File[];
  errors: string[];
}

/**
 * Attachment policy, client-side validation and sequential upload for support tickets.
 */
export function useSupportAttachments(orgId: string) {
  const { t } = useLanguage();
  const { data: policyResponse } = useGetSupportAttachmentPolicy(orgId, {
    query: { staleTime: 60 * 60 * 1000 },
  });
  const policy: SupportAttachmentPolicyDto | undefined = policyResponse?.data;

  /**
   * Splits picked files into those that pass the policy and translated error messages for the rest.
   */
  const validate = useCallback(
    (files: File[]): AttachmentValidation => {
      const accepted: File[] = [];
      const errors: string[] = [];
      for (const file of files) {
        const rejection = validateSupportAttachment(file, policy);
        if (rejection === null) {
          accepted.push(file);
        } else {
          errors.push(
            fillSupportTemplate(t.support.attachmentErrors[rejection], {
              name: file.name,
              size: formatBytes(policy?.maxBytes),
              max: policy?.fileNameMaxLength ?? 0,
            })
          );
        }
      }
      return { accepted, errors };
    },
    [policy, t]
  );

  /**
   * Uploads files one at a time and returns the created attachments and the files that failed.
   */
  const upload = useCallback(
    async (ticketId: string, files: File[]): Promise<AttachmentUploadResult> => {
      const uploaded: SupportAttachmentDto[] = [];
      const failed: File[] = [];
      for (const file of files) {
        try {
          const response = await postSupportTicketAttachments(orgId, ticketId, { file });
          uploaded.push(response.data);
        } catch {
          failed.push(file);
        }
      }
      return { uploaded, failed };
    },
    [orgId]
  );

  const hint = policy ? getAttachmentHint(policy, t.support.newTicket) : undefined;

  return { policy, validate, upload, hint };
}

/**
 * Describes the attachment limits: the size limit plus either the allowed or the blocked extensions.
 */
function getAttachmentHint(policy: SupportAttachmentPolicyDto, labels: SupportTranslations['newTicket']): string {
  const parts = [fillSupportTemplate(labels.attachmentsHint, { size: formatBytes(policy.maxBytes) })];
  const allowed = policy.allowedExtensions ?? [];
  const blocked = policy.blockedExtensions ?? [];
  if (allowed.length > 0) {
    parts.push(fillSupportTemplate(labels.attachmentsAllowedHint, { types: allowed.join(', ') }));
  } else if (blocked.length > 0) {
    parts.push(fillSupportTemplate(labels.attachmentsBlockedHint, { types: blocked.join(', ') }));
  }
  return parts.join(' ');
}
